import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { brDateTime } from "@/lib/labels";
import { toast } from "sonner";
import { ArrowLeft, Clock, CheckCircle2, XCircle, Send, AlertCircle } from "lucide-react";

const QTYPE = { MULTIPLE_CHOICE: "Múltipla escolha", TRUE_FALSE: "Verdadeiro/Falso", ESSAY: "Dissertativa" };
function fmtTimer(s) {
  if (s == null) return "";
  const m = Math.floor(s / 60), ss = s % 60;
  return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export default function StudentEvaluation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [attemptId, setAttemptId] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  const loadResult = useCallback(async (aid) => {
    try { const { data } = await api.get(`/student/evaluation-attempts/${aid}/result`); setResult(data); } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get(`/student/evaluations/${id}`);
    setData(data);
    if (data.current_attempt) {
      setAttemptId(data.current_attempt.id);
      setRemaining(data.current_attempt.remaining_seconds);
      setAnswers(data.current_attempt.answers || {});
      setResult(null);
    } else if (["GRADED", "AWAITING_GRADING", "SUBMITTED"].includes(data.state) && data.last_attempt_id) {
      loadResult(data.last_attempt_id);
    }
  }, [id, loadResult]);
  useEffect(() => { load(); }, [load]);

  const doSubmit = useCallback(async (auto = false) => {
    if (submittingRef.current || !attemptId) return;
    submittingRef.current = true;
    try {
      const payload = { answers: Object.entries(answers).map(([question_id, a]) => ({ question_id, ...a })) };
      await api.post(`/student/evaluation-attempts/${attemptId}/answers`, payload).catch(() => {});
      await api.post(`/student/evaluation-attempts/${attemptId}/submit`);
      if (auto) toast.info("Tempo esgotado — respostas enviadas automaticamente.");
      else toast.success("Avaliação enviada!");
      const aid = attemptId;
      setAttemptId(null); setRemaining(null);
      await loadResult(aid);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { submittingRef.current = false; }
  }, [attemptId, answers, load, loadResult]);

  // Timer
  useEffect(() => {
    if (remaining == null || !attemptId) return;
    if (remaining <= 0) { doSubmit(true); return; }
    const t = setInterval(() => setRemaining((r) => (r == null ? r : r - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining, attemptId, doSubmit]);

  // Autosave every 15s
  useEffect(() => {
    if (!attemptId) return;
    const t = setInterval(() => {
      const payload = { answers: Object.entries(answers).map(([question_id, a]) => ({ question_id, ...a })) };
      api.post(`/student/evaluation-attempts/${attemptId}/answers`, payload).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [attemptId, answers]);

  const start = async () => {
    setBusy(true);
    try {
      const { data: st } = await api.post(`/student/evaluations/${id}/start`);
      setAttemptId(st.attempt_id); setRemaining(st.remaining_seconds);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const setAnswer = (qid, patch) => setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), ...patch } }));

  if (!data) return <p className="text-center text-[#6B655E] py-10">Carregando…</p>;

  return (
    <div className="space-y-4" data-testid="student-evaluation">
      <button onClick={() => nav("/aluno")} className="text-sm text-[#6B655E] inline-flex items-center gap-1" data-testid="eval-back"><ArrowLeft className="w-4 h-4" /> Voltar</button>

      <div className="doxa-card p-5">
        <h1 className="font-display text-xl font-bold">{data.title}</h1>
        {data.description && <p className="text-sm text-[#4A4640] mt-1">{data.description}</p>}
        <p className="text-xs text-[#6B655E] mt-2">{data.time_limit_minutes > 0 ? `${data.time_limit_minutes} min` : "Sem tempo limite"} · {data.attempts_used}/{data.attempts_allowed} tentativa(s)</p>
      </div>

      {/* Result view */}
      {result && !attemptId && (
        <div className="doxa-card p-5" data-testid="eval-result">
          {result.needs_grading ? (
            <div className="flex items-start gap-2 text-[#C05621]"><AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /><p className="text-sm" data-testid="eval-awaiting-grading">Sua avaliação foi enviada e está aguardando correção.</p></div>
          ) : result.show_score ? (
            <div>
              <p className="text-sm text-[#6B655E]">Sua nota</p>
              <p className="font-display text-4xl font-bold" data-testid="eval-final-score">{result.final_score ?? "—"}<span className="text-lg text-[#6B655E]">/10</span></p>
              {result.passed != null && <span className={`doxa-badge ${result.passed ? "doxa-badge-success" : "doxa-badge-critical"} mt-2 inline-block`} data-testid="eval-passed">{result.passed ? "Aprovado" : "Reprovado"}</span>}
            </div>
          ) : <p className="text-sm text-[#4A4640]" data-testid="eval-submitted-noscore">Avaliação enviada. A nota não está disponível para visualização.</p>}

          {result.show_answers && result.questions?.length > 0 && (
            <div className="mt-4 space-y-3" data-testid="eval-answers-review">
              <p className="font-semibold text-sm">Gabarito</p>
              {result.questions.map((q, i) => (
                <div key={q.id} className="border-t border-[#EBE5DB] pt-3">
                  <p className="text-xs text-[#6B655E]">{i + 1} · {QTYPE[q.type]} · {q.points_awarded ?? 0}/{q.points} pts</p>
                  <p className="text-sm font-medium">{q.prompt}</p>
                  {q.type === "MULTIPLE_CHOICE" && <ul className="mt-1 space-y-0.5">{q.options.map((o) => <li key={o.id} className={`text-xs ${o.is_correct ? "text-[#2F855A] font-semibold" : o.id === q.your_option_id ? "text-[#C53030]" : "text-[#6B655E]"}`}>{o.is_correct ? "✓ " : o.id === q.your_option_id ? "✗ " : "• "}{o.text}</li>)}</ul>}
                  {q.type === "TRUE_FALSE" && <p className="text-xs mt-1">Correta: <strong>{q.correct_boolean ? "Verdadeiro" : "Falso"}</strong> · Você: {q.your_boolean == null ? "—" : q.your_boolean ? "Verdadeiro" : "Falso"}</p>}
                  {q.type === "ESSAY" && <><p className="text-xs mt-1 text-[#6B655E]">Sua resposta: {q.your_essay || "—"}</p>{q.grader_comment && <p className="text-xs text-[#2B5F9E] mt-1">Comentário: {q.grader_comment}</p>}</>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Start */}
      {!attemptId && !result && data.state === "AVAILABLE" && (
        <button onClick={start} disabled={busy} className="doxa-btn-primary w-full py-3" data-testid="eval-start-btn">{busy ? "Iniciando…" : "Iniciar avaliação"}</button>
      )}
      {!attemptId && !result && data.state === "AVAILABLE" && data.attempts_used > 0 && (
        <p className="text-xs text-[#6B655E] text-center">Você ainda tem tentativas disponíveis.</p>
      )}
      {!attemptId && !result && data.state === "NOT_OPEN" && <p className="text-sm text-[#6B655E] text-center py-4" data-testid="eval-not-open">{data.opens_at ? `Disponível a partir de ${brDateTime(data.opens_at)}.` : "Esta avaliação ainda não abriu."}</p>}
      {!attemptId && !result && data.state === "CLOSED" && <p className="text-sm text-[#6B655E] text-center py-4" data-testid="eval-closed">Esta avaliação está encerrada.</p>}

      {/* In progress */}
      {attemptId && (
        <>
          <div className="sticky top-16 z-20 doxa-card p-3 flex items-center justify-between" data-testid="eval-timer-bar">
            <span className="text-sm font-medium inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#F75A1B]" /> {remaining == null ? "Sem tempo limite" : fmtTimer(remaining)}</span>
            <button onClick={() => doSubmit(false)} disabled={busy} className="doxa-btn-primary px-4 py-1.5 text-sm inline-flex items-center gap-1.5" data-testid="eval-submit-btn"><Send className="w-4 h-4" /> Enviar</button>
          </div>
          <div className="space-y-3">
            {data.questions.map((q, i) => (
              <div key={q.id} className="doxa-card p-4" data-testid={`eval-question-${q.id}`}>
                <p className="text-xs text-[#6B655E]">{i + 1} · {QTYPE[q.type]} · {q.points} ponto(s)</p>
                <p className="font-medium text-sm mt-1">{q.prompt}</p>
                {q.type === "MULTIPLE_CHOICE" && (
                  <div className="mt-2 space-y-1.5">
                    {q.options.map((o) => (
                      <label key={o.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${answers[q.id]?.selected_option_id === o.id ? "border-[#F75A1B] bg-[#FFF4EF]" : "border-[#E0D9CD]"}`}>
                        <input type="radio" name={`q-${q.id}`} checked={answers[q.id]?.selected_option_id === o.id} onChange={() => setAnswer(q.id, { selected_option_id: o.id })} className="accent-[#F75A1B]" data-testid={`eval-opt-${q.id}-${o.id}`} />
                        <span className="text-sm">{o.text}</span>
                      </label>
                    ))}
                  </div>
                )}
                {q.type === "TRUE_FALSE" && (
                  <div className="mt-2 flex gap-2" data-testid={`eval-tf-${q.id}`}>
                    {[["true", "Verdadeiro", true], ["false", "Falso", false]].map(([k, l, v]) => (
                      <button key={k} onClick={() => setAnswer(q.id, { boolean_answer: v })} className={`flex-1 py-2 rounded-xl text-sm font-medium border ${answers[q.id]?.boolean_answer === v ? "bg-[#F75A1B] text-white border-[#F75A1B]" : "bg-white border-[#E0D9CD]"}`} data-testid={`eval-tf-${q.id}-${k}`}>{l}</button>
                    ))}
                  </div>
                )}
                {q.type === "ESSAY" && (
                  <textarea value={answers[q.id]?.essay_answer || ""} onChange={(e) => setAnswer(q.id, { essay_answer: e.target.value })} rows={4} className="w-full mt-2 px-3 py-2 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]" placeholder="Escreva sua resposta…" data-testid={`eval-essay-${q.id}`} />
                )}
              </div>
            ))}
          </div>
          <button onClick={() => doSubmit(false)} disabled={busy} className="doxa-btn-primary w-full py-3 inline-flex items-center justify-center gap-2" data-testid="eval-submit-bottom"><Send className="w-4 h-4" /> Enviar avaliação</button>
        </>
      )}
    </div>
  );
}
